import { useEffect, useState } from 'react'
import { useRefValue } from './useRefValue'

interface FocusTrapProps {
  isOpen: boolean
  selector: string
  onEscape?: () => void
}

const getFocusableElements = (selector: string) =>
  !!selector
    ? document
        .querySelector(selector || '')
        ?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
    : []

export const focusFirstElement = (selector: string) => {
  const focusableElements = getFocusableElements(selector)
  const firstElement = focusableElements?.[0] as HTMLElement | undefined

  if (!!firstElement) firstElement.focus()
}

export const useFocusTrap = ({ isOpen = false, selector = '', onEscape }: FocusTrapProps): void => {
  const callbackRef = useRefValue({ onEscape })
  const [activeLastElement, setActiveLastElement] = useState<HTMLElement | null>(null)

  const hasEscape = !!onEscape

  useEffect(() => {
    if (hasEscape) {
      const { onEscape } = callbackRef.current

      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isOpen) {
          onEscape?.()
        }
      }

      document.addEventListener('keydown', handleKeyPress)

      return () => {
        document.removeEventListener('keydown', handleKeyPress)
      }
    }
  }, [callbackRef, hasEscape, isOpen])

  useEffect(() => {
    const trapFocus = (event: KeyboardEvent) => {
      if (selector) {
        const focusableElements = getFocusableElements(selector)
        const length = focusableElements?.length || 0

        const firstElement = focusableElements?.[0] as HTMLElement | undefined
        const lastElement = focusableElements?.[length > 1 ? length - 1 : 0] as
          | HTMLElement
          | undefined

        if (event?.key === 'Tab' && firstElement && lastElement) {
          if (event.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement?.focus()
              event.preventDefault()
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus()
              event.preventDefault()
            }
          }
        }
      }
    }

    if (isOpen) {
      //handle trap focus
      document.addEventListener('keydown', trapFocus)
    } else {
      document.removeEventListener('keydown', trapFocus)
    }

    return () => {
      document.removeEventListener('keydown', trapFocus)
    }
  }, [isOpen, selector])

  useEffect(() => {
    setActiveLastElement(document.activeElement as HTMLElement)
  }, [])

  useEffect(() => {
    return () => {
      if (isOpen) activeLastElement?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLastElement])
}
